import React, { useContext, useEffect, useRef } from 'react';
import {
  UseFormSetValue,
  FieldValues,
  FieldErrors,
  Control,
} from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { FormQuestion, FormQuestionType } from 'api/form/types';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { BodyLong01, Caption01 } from 'components/styles';
import { VariablesContext } from 'contexts/variables';
import styles from './rte.module.css';

export interface FormFieldProps {
  field: FormQuestion;
  control: Control<FieldValues>;
  setValue?: UseFormSetValue<FieldValues>;
  errors?: FieldErrors<FieldValues>;
  disabled: boolean;
  renderRowErrorObject?: { [key: number]: boolean };
}

export const FormItemWrapper = styled.div`
  background: var(--white-100);
  div.form-error + div.form-description {
    display: none;
  }
`;

const FormItemDescription = styled.div`
  ${Caption01};
  color: var(--gray-80);
  margin-top: var(--spacing-02);
`;

export const FormItemDesc = ({
  label,
}: {
  label: string;
}): JSX.Element | null => {
  const variableContext = useContext(VariablesContext);

  return label ? (
    <FormItemDescription className="form-description">
      <RichTextEditor
        data={label ?? ''}
        readonly
        suppressCloudServices
        className={styles['form-question']}
        allowSimpleVariables={variableContext?.allowSimpleVariables}
        resolveVariablesToFallback={variableContext?.resolveVariablesToFallback}
      />
    </FormItemDescription>
  ) : null;
};

const FormLabel = styled.div<{ required: boolean }>`
  ${BodyLong01};
  display: flex;
  gap: var(--spacing-01);
  ${ifProp(
    'required',
    css`
      &:after {
        color: var(--red-60);
        content: ' *';
      }
    `,
  )};
`;

export const FormItemLabel = ({
  label,
  required,
}: {
  label: string;
  required: boolean;
}): JSX.Element => {
  const variableContext = useContext(VariablesContext);

  return (
    <FormLabel required={required}>
      <RichTextEditor
        data={label ?? ''}
        readonly
        suppressCloudServices
        className={styles['form-question']}
        allowSimpleVariables={variableContext?.allowSimpleVariables}
        resolveVariablesToFallback={variableContext?.resolveVariablesToFallback}
      />
    </FormLabel>
  );
};

const ErrorMessage = styled.div`
  ${Caption01};
  margin-top: var(--spacing-01);
  color: var(--red-60);
`;

export const FormErrorMessage = ({
  errorMessageKey,
}: {
  errorMessageKey: string;
}): JSX.Element => {
  return (
    <ErrorMessage>
      <FormattedMessage id={errorMessageKey} />
    </ErrorMessage>
  );
};

export const FieldsErrorRenderer = ({
  errors,
  questionId,
  questionType,
}: {
  errors: FieldErrors<FieldValues> | undefined;
  questionId: number;
  questionType?: FormQuestionType;
}): JSX.Element | null => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      errors &&
      errors[questionId] &&
      Object.keys(errors)[0] === questionId.toString()
    ) {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [errors, questionId]);

  let errorMessage;

  switch (errors && errors[questionId] && errors[questionId].type) {
    case 'required':
      errorMessage = [
        questionType && FormQuestionType.CheckBoxGrid,
        FormQuestionType.MultipleChoiceGrid,
      ].includes(questionType)
        ? 'matrix.input.required'
        : 'input.required';
      break;
    case 'invalidEmail':
      errorMessage = 'input.email';
      break;
    case 'invalidPhoneNumber':
      errorMessage = 'form_templates.invalid_phone_number';
      break;
    case 'filesRequired':
      errorMessage = 'input.required';
      break;
    default:
      errorMessage = '';
  }

  return errorMessage ? (
    <div ref={ref} className="form-error">
      <FormErrorMessage errorMessageKey={errorMessage} />
    </div>
  ) : null;
};
